import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, type InviteTokenInfo } from '../services/api';
import { appState } from '../store/AppState';
import './shared/SignInOptions';

@customElement('invite-redemption')
export class InviteRedemption extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property()
  token = '';

  @state()
  private loading = true;

  @state()
  private redeeming = false;

  @state()
  private tokenInfo: InviteTokenInfo | null = null;

  @state()
  private error = '';

  @state()
  private redeemed = false;

  @state()
  private redeemedTeamName = '';

  @state()
  private autoRedeemAttempted = false;

  private unsubscribeAppState: (() => void) | null = null;

  private get isAuthenticated(): boolean {
    return appState.isAuthenticated;
  }

  private get isAuthLoading(): boolean {
    return appState.isLoading;
  }

  async connectedCallback() {
    super.connectedCallback();

    // Subscribe to auth state changes — auto-redeem when user becomes authenticated.
    // Keep the unsubscribe handle so we don't leak the callback (and the closed-over
    // `this`) after the component is removed.
    this.unsubscribeAppState = appState.subscribe(() => {
      this.requestUpdate();
      this.maybeAutoRedeem();
    });

    await this.validateToken();
    this.maybeAutoRedeem();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribeAppState?.();
    this.unsubscribeAppState = null;
  }

  // Auto-redeem the invite as soon as the user is authenticated and the token
  // has been validated. This prevents the common case where a user signs in
  // via the invite link but never clicks the second "Join Team" button.
  private maybeAutoRedeem() {
    if (
      !this.autoRedeemAttempted &&
      this.isAuthenticated &&
      !this.isAuthLoading &&
      this.tokenInfo?.valid &&
      !this.redeemed &&
      !this.redeeming
    ) {
      this.autoRedeemAttempted = true;
      this.handleRedeem();
    }
  }

  private async validateToken() {
    if (!this.token) {
      this.error = 'No invite token provided.';
      this.loading = false;
      return;
    }

    try {
      const info = await api.validateInvite(this.token);
      if (info.valid) {
        this.tokenInfo = info;
      } else {
        this.error = 'This invite link is invalid or has expired. Ask your league administrator for a new link.';
      }
    } catch {
      this.error = 'Unable to validate invite link. Please try again later.';
    } finally {
      this.loading = false;
    }
  }

  private async handleRedeem() {
    if (this.redeeming || !this.token) return;

    this.redeeming = true;
    this.error = '';

    try {
      await api.redeemInvite(this.token);
      this.redeemed = true;
      this.redeemedTeamName = this.tokenInfo?.team_name ?? '';

      // Redirect to home after short delay
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      if (message.includes('expired')) {
        this.error = 'This invite link has expired. Ask your league administrator for a new link.';
      } else {
        this.error = 'Failed to join team. Please try again.';
      }
    } finally {
      this.redeeming = false;
    }
  }

  render() {
    if (this.loading || this.isAuthLoading) {
      return html`
        <div class="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <span class="loading loading-spinner loading-lg text-primary"></span>
          <p class="text-base-content/70">Validating invite link...</p>
        </div>
      `;
    }

    if (this.error) {
      return this.renderError();
    }

    if (this.redeemed) {
      return this.renderSuccess();
    }

    if (!this.isAuthenticated) {
      return this.renderSignIn();
    }

    return this.renderConfirm();
  }

  private renderError() {
    return html`
      <div class="flex flex-col items-center justify-center min-h-[60vh] gap-6 p-4">
        <div class="card bg-base-100 shadow-xl max-w-md w-full">
          <div class="card-body items-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 text-error mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 class="card-title">Invalid Invite</h2>
            <p class="text-base-content/70">${this.error}</p>
            <div class="card-actions mt-4">
              <a href="/" class="btn btn-primary">Go Home</a>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderSuccess() {
    return html`
      <div class="flex flex-col items-center justify-center min-h-[60vh] gap-6 p-4">
        <div class="card bg-base-100 shadow-xl max-w-md w-full">
          <div class="card-body items-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 text-success mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <h2 class="card-title">Welcome!</h2>
            <p class="text-base-content/70">
              You've joined <strong>${this.redeemedTeamName}</strong>.
              Redirecting...
            </p>
            <span class="loading loading-dots loading-sm mt-2"></span>
          </div>
        </div>
      </div>
    `;
  }

  private renderSignIn() {
    const teamName = this.tokenInfo?.team_name ?? 'this team';
    return html`
      <div class="flex flex-col items-center justify-center min-h-[60vh] gap-6 p-4">
        <div class="card bg-base-100 shadow-xl max-w-md w-full">
          <div class="card-body items-center text-center py-10">
            <div class="w-20 h-20 rounded-sm flex items-center justify-center mb-4 overflow-hidden">
              <img src="/static/image/logo-no-text.webp" alt="Baseball Manitoba" class="w-full h-full object-contain" />
            </div>
            <h2 class="card-title text-xl">Join ${this.tokenInfo?.team_name}</h2>
            <p class="text-base-content/70 mb-4">
              Sign in or create an account to join <strong>${teamName}</strong> on Baseball Manitoba Pitch Counter.
            </p>
            <sign-in-options></sign-in-options>
          </div>
        </div>
      </div>
    `;
  }

  private renderConfirm() {
    // Auto-redeem is in progress (or about to fire). If for some reason it
    // doesn't kick in, expose a fallback button so the user isn't stuck.
    return html`
      <div class="flex flex-col items-center justify-center min-h-[60vh] gap-6 p-4">
        <div class="card bg-base-100 shadow-xl max-w-md w-full">
          <div class="card-body items-center text-center">
            ${this.error
              ? html`
                  <div class="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h2 class="card-title text-xl">Couldn't join ${this.tokenInfo?.team_name}</h2>
                  <p class="text-base-content/70 mb-4">${this.error}</p>
                  <div class="card-actions gap-3">
                    <a href="/" class="btn btn-ghost">Cancel</a>
                    <button class="btn btn-primary" @click=${this.handleRedeem} ?disabled=${this.redeeming}>
                      Try again
                    </button>
                  </div>
                `
              : html`
                  <span class="loading loading-spinner loading-lg text-primary mb-2"></span>
                  <h2 class="card-title text-xl">Joining ${this.tokenInfo?.team_name}…</h2>
                  <p class="text-base-content/70">Hang tight, this only takes a moment.</p>
                `}
          </div>
        </div>
      </div>
    `;
  }
}
